import { apiAuthFetch } from "@/lib/authFetch";

/**
 * Continuation envelope returned by the result endpoint when a blocking approval tool
 * was signalled. The `{ continuation: true }` shape tells the frontend that the agent
 * loop continues in the same turn — the stream is still open, events will keep flowing.
 * No action needed from the SPA beyond closing the sidebar.
 */
export type ChatContinuation = {
  continuation: boolean;
};

/**
 * Typed outcome of a tool-call result POST. The 410 branch distinguishes
 * "this previewId is no longer known to the server" (TTL expired, API
 * restart, or stale toolCallId after the assistant re-emitted the approval)
 * from generic 4xx/5xx failure — so the caller can show "stale approval,
 * open a new one" instead of a false-positive "saved" toast.
 *
 * The `ok` variant carries:
 * - `continuation`: populated on the Approve path when the blocking tool
 *   was signalled (the agent loop continues in the same turn).
 */
export type ToolCallResultOutcome =
  | { kind: "ok"; continuation: ChatContinuation | null }
  | { kind: "stale" }
  | { kind: "error"; status: number };

/**
 * POST the user's answer payload to the tool-call result endpoint.
 * Returns a discriminated union so callers branch on semantic outcome,
 * not raw HTTP status. 410 maps to `stale`; 2xx to `ok`; everything else
 * to `error`.
 */
export async function submitToolCallResult(
  toolCallId: string,
  payload: object,
): Promise<ToolCallResultOutcome> {
  const res = await apiAuthFetch(`api/chat/tool-call/${toolCallId}/result`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (res.ok) {
    let continuation: ChatContinuation | null = null;
    try {
      const text = await res.text();
      if (text.trim().length > 0) {
        const parsed = JSON.parse(text);
        // Blocking approval tool continuation signal.
        if (parsed?.continuation === true) {
          continuation = { continuation: true };
        }
      }
    } catch {
      // Tolerate non-JSON 200 bodies (the unwrapping is best-effort UX; if the
      // body is malformed for some reason we still treat the result as OK and the
      // user can re-engage via a follow-up message — the verdict is already
      // stamped server-side).
    }
    return { kind: "ok", continuation };
  }
  if (res.status === 410) return { kind: "stale" };
  return { kind: "error", status: res.status };
}
